/**
 * The editor's schema — what a Twasta business document is allowed to contain.
 *
 * Cloned in approach from OpenDraft's `editor/extensions`, with every
 * screenplay node left behind: there is no scene heading, character, dialogue,
 * parenthetical, transition, dual dialogue, act break, title page or cast list
 * here, and no import of anything from that project. What was worth keeping is
 * the pattern — one small extension per capability, attributes that survive a
 * round trip through stored JSON, and a schema narrow enough that a document
 * cannot contain a node the layout engine has never heard of.
 *
 * What a business document needs that a screenplay never did — tables, nested
 * numbered lists with real legal numbering, a document control block bound to
 * the record — is added here.
 */

import {
  Extension, Mark, Node, mergeAttributes,
  type ChainedCommands, type RawCommands,
} from '@tiptap/core'
import Paragraph from '@tiptap/extension-paragraph'
import OrderedList from '@tiptap/extension-ordered-list'
import Image from '@tiptap/extension-image'
import { Table } from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import { STYLE_MENU } from './types'

/** Paragraph styles a user may apply, as a set for cheap validation. */
const STYLE_IDS = new Set<string>(STYLE_MENU)

/**
 * A paragraph carrying the template element it is styled by.
 *
 * A heading knows its own element from its level; a paragraph does not, so it
 * carries one. `styleId` is stored on the node rather than derived from a class
 * because the class is presentation — a document re-opened in a different
 * template must still know that this paragraph is a Caption.
 */
export const DocParagraph = Paragraph.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      styleId: {
        default: 'body',
        parseHTML: (element) => {
          const value = element.getAttribute('data-style') || 'body'
          return STYLE_IDS.has(value) ? value : 'body'
        },
        renderHTML: (attributes) => {
          const value = String(attributes.styleId || 'body')
          return { 'data-style': STYLE_IDS.has(value) ? value : 'body' }
        },
      },
    }
  },
})

/**
 * An ordered list that remembers its numbering scheme.
 *
 * Without this, "1.1.1" numbering is something an author types and then
 * maintains by hand — which is why business documents so often have two
 * sections numbered 4.2. The scheme is a property of the list; the marker text
 * is computed in `layout.listMarker` so the editor, the page and the PDF all
 * derive it the same way rather than reading three different CSS counters.
 */
export const DocOrderedList = OrderedList.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      numbering: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-numbering'),
        renderHTML: (attributes) => (attributes.numbering
          ? { 'data-numbering': attributes.numbering } : {}),
      },
    }
  },
})

/**
 * The document control block.
 *
 * An atom: its rows are RESOLVED from the record on every open and every
 * render (`authoring.resolve_control_values`), so there is nothing inside it
 * for a cursor to sit in. `values` holds only what a template marked as the
 * author's to fill in — everything else would be a typed copy of a fact the
 * repository already knows, and a typed copy is the thing that goes stale.
 */
export const ControlSection = Node.create({
  name: 'controlSection',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      values: {
        default: {},
        parseHTML: (element) => {
          const raw = element.getAttribute('data-values')
          if (!raw) return {}
          try {
            const parsed = JSON.parse(raw)
            return parsed && typeof parsed === 'object' ? parsed : {}
          } catch {
            // A corrupted attribute must not stop the document opening — the
            // author-filled cells come back empty and everything bound to the
            // record still resolves.
            return {}
          }
        },
        renderHTML: (attributes) => ({
          'data-values': JSON.stringify(attributes.values || {}),
        }),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'div[data-control-section]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, {
      'data-control-section': 'true',
    })]
  },

  addCommands() {
    return {
      insertControlSection: () => ({ commands }: {
        commands: { insertContent: (value: unknown) => boolean }
      }) => commands.insertContent({ type: this.name, attrs: { values: {} } }),
    } as Partial<RawCommands>
  },
})

/** An explicit "start a new page here". */
export const PageBreak = Node.create({
  name: 'pageBreak',
  group: 'block',
  atom: true,
  selectable: true,

  parseHTML() {
    return [{ tag: 'div[data-page-break]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, {
      'data-page-break': 'true', class: 'twa-doc-page-break',
    })]
  },

  addCommands() {
    return {
      insertPageBreak: () => ({ commands }: {
        commands: { insertContent: (value: unknown) => boolean }
      }) => commands.insertContent({ type: this.name }),
    } as Partial<RawCommands>
  },
})

/**
 * An image that keeps the size the author gave it.
 *
 * The layout engine works in points and needs a real box, so the dimensions
 * ride on the node rather than in a style attribute a sanitiser would strip.
 */
export const DocImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (element) => element.getAttribute('width'),
        renderHTML: (attributes) => (attributes.width
          ? { width: attributes.width } : {}),
      },
      height: {
        default: null,
        parseHTML: (element) => element.getAttribute('height'),
        renderHTML: (attributes) => (attributes.height
          ? { height: attributes.height } : {}),
      },
    }
  },
})

/**
 * A point size on a text run.
 *
 * Rides on `textStyle` — the same mark `color` and `fontFamily` use — so three
 * inline properties share one mark instead of stacking three spans around every
 * word.
 */
export const FontSize = Extension.create({
  name: 'fontSize',

  addOptions() {
    return { types: ['textStyle'] }
  },

  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        fontSize: {
          default: null,
          parseHTML: (element) => {
            const raw = element.style.fontSize
            if (!raw) return null
            const value = Number.parseFloat(raw)
            return Number.isFinite(value) && value > 0 ? value : null
          },
          renderHTML: (attributes) => (attributes.fontSize
            ? { style: `font-size: ${attributes.fontSize}pt` } : {}),
        },
      },
    }]
  },

  addCommands() {
    return {
      setFontSize: (size: number) => ({ chain }: {
        chain: () => ChainedCommands
      }) => chain().setMark('textStyle', { fontSize: size }).run(),
      unsetFontSize: () => ({ chain }: { chain: () => ChainedCommands }) => chain()
        .setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
      // Through `unknown`: tiptap 3's text-style declares its own string-sized
      // `setFontSize` on `RawCommands`. Ours keeps a NUMBER of points, which is
      // what stored documents hold, and the stock FontSize is never registered.
    } as unknown as Partial<RawCommands>
  },
})

/**
 * Subscript and superscript.
 *
 * Written here rather than pulled from `@tiptap/extension-subscript` for one
 * reason that is not taste: this package's PAGE and its PDF are drawn by
 * `layout.ts` and `pdfExporter.ts`, not by the browser, so a raised character
 * has to be a run the layout engine can measure and place at a shifted
 * baseline. A mark this package owns is a mark those two are guaranteed to
 * know about; a dependency's would render on screen and vanish from the filed
 * PDF, which is the one failure this editor is built not to have.
 *
 * The two exclude each other, because text is either raised or lowered and
 * a character carrying both has no baseline to sit on.
 */
function scriptMark(name: 'subscript' | 'superscript', tag: 'sub' | 'sup',
  other: string, value: string) {
  return Mark.create({
    name,
    excludes: other,

    parseHTML() {
      return [
        { tag },
        {
          style: 'vertical-align',
          getAttrs: (raw) => (String(raw) === value ? {} : false),
        },
      ]
    },

    renderHTML({ HTMLAttributes }) {
      return [tag, mergeAttributes(HTMLAttributes), 0]
    },
  })
}

export const Subscript = scriptMark('subscript', 'sub', 'superscript', 'sub')
export const Superscript = scriptMark('superscript', 'sup', 'subscript', 'super')

/**
 * The commands and shortcuts for the two marks above.
 *
 * Separate from the marks themselves so that toggling one CLEARS the other in
 * a single transaction. Left to `excludes` alone, ProseMirror drops the
 * conflicting mark only where the new one is applied, which on a partial
 * selection leaves a word half raised and half lowered.
 */
export const ScriptCommands = Extension.create({
  name: 'scriptCommands',

  addCommands() {
    const toggle = (mark: 'subscript' | 'superscript', opposite: string) => () => (
      ({ chain, editor }: { chain: () => ChainedCommands; editor: { isActive: (n: string) => boolean } }) => {
        if (editor.isActive(mark)) return chain().unsetMark(mark).run()
        return chain().unsetMark(opposite).setMark(mark).run()
      }
    )
    return {
      toggleSubscript: toggle('subscript', 'superscript'),
      toggleSuperscript: toggle('superscript', 'subscript'),
      unsetScript: () => ({ chain }: { chain: () => ChainedCommands }) => chain()
        .unsetMark('subscript').unsetMark('superscript').run(),
    } as unknown as Partial<RawCommands>
  },

  addKeyboardShortcuts() {
    const editor = this.editor as unknown as {
      commands: { toggleSubscript: () => boolean; toggleSuperscript: () => boolean }
    }
    return {
      // The pair Word and every browser editor use, so an author who knows one
      // knows this.
      'Mod-,': () => editor.commands.toggleSubscript(),
      'Mod-.': () => editor.commands.toggleSuperscript(),
    }
  },
})

/**
 * Formatting the template LOCKS.
 *
 * In `enforce` mode a template's rules are the document's formatting and a user
 * may not add inline overrides — that is what makes a controlled document look
 * the same whoever wrote it. `allow_format_override` on an individual rule
 * carves out the elements where local emphasis is still wanted, so "enforce"
 * does not have to mean "no bold anywhere".
 *
 * The guard is a keyboard/command-level refusal, not a sanitiser: existing
 * marks in an older document are left alone, because silently stripping an
 * approved document's emphasis on open would be a worse failure than an
 * inconsistency.
 */
export interface FormatLockOptions {
  /** Returns true when an inline format may be applied at the cursor. */
  allow: () => boolean
}

export const FormatLock = Extension.create<FormatLockOptions>({
  name: 'formatLock',

  addOptions() {
    return { allow: () => true }
  },

  addKeyboardShortcuts() {
    const block = () => !this.options.allow()
    return {
      'Mod-b': block,
      'Mod-i': block,
      'Mod-u': block,
      'Mod-Shift-x': block,
      'Mod-,': block,
      'Mod-.': block,
    }
  },
})

// ── Document Form (obj_type `E`) atoms ────────────────────────────────────
//
// Three nodes, and between them they are the whole difference between a
// document you READ and a document you FILL IN.
//
// **A node carries only a KEY.** Type, tolerance, mandatory, lookup and
// validations live in the template's field schema, never on the node. That is
// not tidiness: a QA user editing body text would otherwise be able to change
// a tolerance, with no change control over the thing that changed.
//
// **A step is not a node.** It is `attrs.stepKey` on an ordinary heading or
// list item, so the existing legal numbering (`layout.listMarker`) produces
// "4.2.1" and the printed record can never disagree with the screen.

/**
 * How wide an atom is, and what happens when the value does not fit.
 *
 * Shared by the entry box and the merge field, as ONE definition, because the
 * two have to mean the same thing by the same numbers: a column of a table
 * routinely holds a box on one row and a shown value on the next, and an
 * author sizing that column is sizing both. Two copies of these attributes
 * would be two chances for the units to drift apart.
 *
 * `width` is in CHARACTERS and `widthPx` in PIXELS, at most one ever set, and
 * both `0` means AUTO — the atom takes whatever the surrounding text or the
 * table cell gives it, which is what everything written before widths existed
 * does. `wrap` decides what a value too long for that width does: flow onto
 * another line, or be cut off.
 *
 * They live on the NODE, not the field, because they describe where the atom
 * SITS: the same field placed twice can reasonably be narrow in a table cell
 * and wide in a paragraph. They also cannot change what is captured, which is
 * the line the "a node carries only a key" rule is actually drawn to protect.
 */
function sizeAttributes() {
  return {
    /*
     * How wide it is, IN CHARACTERS.
     *
     * Characters rather than pixels because this number has to mean the same
     * thing on two surfaces that measure differently: the screen renders it
     * as `ch`, which CSS defines against the element's own font, and the
     * layout engine rules — or clips — exactly that many characters. A pixel
     * width would be a screen measurement the PDF had to guess at, and the
     * two would drift apart at the first font change.
     */
    width: {
      default: 0,
      parseHTML: (element: HTMLElement) =>
        Number(element.getAttribute('data-width') || 0) || 0,
      renderHTML: (attributes: Record<string, unknown>) =>
        (Number(attributes.width) > 0
          ? { 'data-width': String(Math.round(Number(attributes.width))) } : {}),
    },
    /**
     * How wide it is, IN PIXELS — the other way to say it.
     *
     * Characters are the honest unit for a ruled blank on a printed page, and
     * they are the wrong unit for somebody laying out a column: a column is a
     * width, and the width they mean is the one they can see. So an atom may
     * be given a pixel width instead, converted to points for the page at
     * 0.75pt/px — the same ratio the column widths a table already stores
     * (`colwidth`) are converted at, so an atom sized to match a column and
     * the column itself stay the same width.
     *
     * At most one of the two is ever set: setting either clears the other,
     * because "12 characters and also 90 pixels" has no answer.
     */
    widthPx: {
      default: 0,
      parseHTML: (element: HTMLElement) =>
        Number(element.getAttribute('data-width-px') || 0) || 0,
      renderHTML: (attributes: Record<string, unknown>) =>
        (Number(attributes.widthPx) > 0
          ? { 'data-width-px': String(Math.round(Number(attributes.widthPx))) }
          : {}),
    },
    /**
     * It holds several lines, and text too long for the width wraps.
     *
     * On an ENTRY box: a single-line input scrolls its own content sideways,
     * so a paragraph typed into one is a paragraph nobody can read back and
     * nobody can see on the printed page either. This makes it a real
     * multi-line box.
     *
     * On a MERGE field, it is the answer to the same question from the other
     * side — the value is already as long as it is, and the author is saying
     * whether a long one takes more lines or is cut off at the width. Off with
     * a width set means CUT OFF, which is what a column of a table wants: one
     * line per row, whatever is in it.
     */
    wrap: {
      default: false,
      parseHTML: (element: HTMLElement) =>
        element.getAttribute('data-wrap') === 'true',
      renderHTML: (attributes: Record<string, unknown>) =>
        (attributes.wrap ? { 'data-wrap': 'true' } : {}),
    },
  }
}

/**
 * A value merged in from the record — never editable.
 *
 * `format` names a display format (the same vocabulary `<DisplayCell>` uses);
 * `fallback` is what prints when the binding resolved to nothing, which is a
 * real state and not the same as an empty string somebody typed.
 */
export const MergeField = Node.create({
  name: 'mergeField',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      key: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-key') || '',
        renderHTML: (attributes) => ({ 'data-key': String(attributes.key || '') }),
      },
      format: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-format') || '',
        renderHTML: (attributes) => (attributes.format
          ? { 'data-format': String(attributes.format) } : {}),
      },
      fallback: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-fallback') || '',
        renderHTML: (attributes) => (attributes.fallback
          ? { 'data-fallback': String(attributes.fallback) } : {}),
      },
      // A shown value is sized the same way a box is — see `sizeAttributes`.
      // Auto (neither width set) is the default and is exactly what every
      // merge field written before this did: it flows with the text.
      ...sizeAttributes(),
    }
  },

  parseHTML() {
    return [{ tag: 'span[data-merge-field]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, {
      'data-merge-field': 'true', class: 'twa-doc-merge',
    })]
  },

  addCommands() {
    return {
      insertMergeField: (attrs: {
        key: string; format?: string; fallback?: string
        width?: number; widthPx?: number; wrap?: boolean
      }) =>
        ({ commands }: { commands: { insertContent: (value: unknown) => boolean } }) =>
          commands.insertContent({ type: this.name, attrs }),
    } as Partial<RawCommands>
  },
})

/**
 * A box the operator fills at RUN time.
 *
 * Inline rather than block on purpose: "Weigh the API and record the weight
 * ___ kg" is one sentence, and a block control would break it into three. It
 * is an atom, so the cursor cannot get inside it in the TEMPLATE editor — what
 * a template author edits is where the field sits, never what is in it.
 */
export const EntryField = Node.create({
  name: 'entryField',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      key: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-key') || '',
        renderHTML: (attributes) => ({ 'data-key': String(attributes.key || '') }),
      },
      ...sizeAttributes(),
    }
  },

  parseHTML() {
    return [{ tag: 'span[data-entry-field]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, {
      'data-entry-field': 'true', class: 'twa-doc-entry',
    })]
  },

  addCommands() {
    return {
      insertEntryField: (attrs: { key: string; width?: number; wrap?: boolean }) =>
        ({ commands }: { commands: { insertContent: (value: unknown) => boolean } }) =>
          commands.insertContent({ type: this.name, attrs }),
    } as Partial<RawCommands>
  },
})

/**
 * A named, ordered, empty signature slot — the mirror of an unsigned `/Sig`
 * field with no `/V`.
 *
 * A BLOCK atom: a signature is a thing on the page with a rule under it and a
 * name and a date beside it, not a word in a sentence. `slotKey` is what the
 * signing machinery fills; everything about WHO may fill it and what signing
 * it means lives in the template's step schema.
 */
export const SignatureBlock = Node.create({
  name: 'signatureBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      slotKey: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-slot') || '',
        renderHTML: (attributes) => ({ 'data-slot': String(attributes.slotKey || '') }),
      },
      label: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-label') || '',
        renderHTML: (attributes) => (attributes.label
          ? { 'data-label': String(attributes.label) } : {}),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'div[data-signature-block]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, {
      'data-signature-block': 'true', class: 'twa-doc-signature',
    })]
  },

  addCommands() {
    return {
      insertSignatureBlock: (attrs: { slotKey: string; label?: string }) =>
        ({ commands }: { commands: { insertContent: (value: unknown) => boolean } }) =>
          commands.insertContent({ type: this.name, attrs }),
    } as Partial<RawCommands>
  },
})

/**
 * A table that can be drawn TIGHT.
 *
 * `dense` is the one thing about a table that is neither its content nor a
 * column width: how much air there is inside a cell. A document's line table is
 * the place it matters — four points of padding on every side of every cell is
 * right for a paragraph in a box and wasteful on forty lines of figures, where
 * it is the difference between one page and two.
 *
 * It is on the TABLE rather than in the style template because it belongs to
 * this table: an author tightens the lines and leaves the terms table alone.
 * Both renderers read it — `layoutTable` measures with it, `templateCss` draws
 * with it — so the page and the screen tighten together.
 */
export const DocTable = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      dense: {
        default: false,
        parseHTML: (element: HTMLElement) =>
          element.getAttribute('data-dense') === 'true',
        renderHTML: (attributes: Record<string, unknown>) =>
          (attributes.dense ? { 'data-dense': 'true' } : {}),
      },
    }
  },
})

/**
 * A table row that may REPEAT — the grid shape of the loop (§6h).
 *
 * The block repeat below is the general form: its children are the template for
 * one row, so a table inside one is N tables, ruled apart and with the column
 * headings either missing or reprinted per line. That is the wrong shape for
 * the thing most documents want — an invoice's lines under ONE set of headings,
 * as one grid.
 *
 * A table cannot CONTAIN a repeat block: `prosemirror-tables` maps every child
 * of a table as a row, and a block between two rows breaks the cell map that
 * column resizing, cell selection and every table command read. So the loop
 * rides on the row instead. `repeatKey` names the repeat field, exactly as the
 * block's `key` does, and everything else about the loop — which form it runs
 * over, in what order, whether rows may be added — stays in `fields[]` where
 * change control can see it.
 *
 * `repeatIndex` is never authored and never stored. The run-time view stamps it
 * on each instance it lays out, so an atom inside a row can say WHICH row it is
 * in without a React context per row — see `er/nodeViews.tsx`.
 */
export const DocTableRow = TableRow.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      repeatKey: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          element.getAttribute('data-repeat-key') || null,
        renderHTML: (attributes: Record<string, unknown>) =>
          (attributes.repeatKey
            ? { 'data-repeat-key': String(attributes.repeatKey) }
            : {}),
      },
      /**
       * The totals row a repeating table carries, if the author asked for one.
       *
       * It is an ORDINARY row — what makes it a footer is that it sits after
       * the repeating one, so it prints once, after the last line. The
       * attribute exists so that turning the control off removes the row that
       * control added and never a footer somebody wrote themselves.
       */
      totalsFor: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          element.getAttribute('data-totals-for') || null,
        renderHTML: (attributes: Record<string, unknown>) =>
          (attributes.totalsFor
            ? { 'data-totals-for': String(attributes.totalsFor) }
            : {}),
      },
      repeatIndex: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const raw = element.getAttribute('data-repeat-index')
          return raw == null || raw === '' ? null : Number(raw)
        },
        renderHTML: (attributes: Record<string, unknown>) =>
          (attributes.repeatIndex == null
            ? {}
            : { 'data-repeat-index': String(attributes.repeatIndex) }),
      },
    }
  },
})

/**
 * The add / remove control a repeating table carries at run time.
 *
 * Only the operator's view ever holds one: it is stamped into the EXPANDED
 * document beside the rows, never authored and never saved, which is why the
 * printed page knows nothing about it. It is a node rather than a button
 * floated over the table because a table's rows are laid out by the browser —
 * anything positioned over them is wrong the moment a cell wraps.
 */
export const RepeatControl = Node.create({
  name: 'repeatControl',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: false,
  draggable: false,

  addAttributes() {
    return {
      key: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-key') || '',
        renderHTML: (attributes) => ({ 'data-key': String(attributes.key || '') }),
      },
      action: {
        default: 'add',
        parseHTML: (element) => element.getAttribute('data-action') || 'add',
        renderHTML: (attributes) => ({
          'data-action': String(attributes.action || 'add'),
        }),
      },
      index: {
        default: -1,
        parseHTML: (element) => Number(element.getAttribute('data-index') || -1),
        renderHTML: (attributes) => ({
          'data-index': String(attributes.index ?? -1),
        }),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'span[data-repeat-control]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, {
      'data-repeat-control': 'true', class: 'twa-doc-repeat-control',
    })]
  },
})

/**
 * A block that is laid out ONCE PER ROW of the form its field names — the
 * `xsl:for-each` (§6h).
 *
 * A CONTAINER, not an atom, and that is the whole design: what an author puts
 * inside it is the template for one row, written as ordinary document content —
 * a table row, a numbered paragraph, a heading and three entry boxes — so a
 * repeating block is laid out, paginated and printed by the code that already
 * does all three, with no second renderer and no row-shaped special case.
 *
 * It carries only a `key`, like every other document-form node: which form it
 * loops over, in what order, under what filter and whether rows may be added
 * all live in `fields[]`, where change control can see them.
 *
 * **Anchor-opaque, like `ControlSection`.** The layout engine emits N copies of
 * the children while the ProseMirror document holds one, so nothing inside gets
 * its own pagination anchor — `collectAnchors` skips it for exactly the same
 * reason, and the two must stay in step or page separators land in the wrong
 * place.
 */
export const RepeatBlock = Node.create({
  name: 'repeatBlock',
  group: 'block',
  content: 'block+',
  defining: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      key: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-key') || '',
        renderHTML: (attributes) => ({ 'data-key': String(attributes.key || '') }),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'div[data-repeat-block]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, {
      'data-repeat-block': 'true', class: 'twa-doc-repeat',
    }), 0]
  },

  addCommands() {
    return {
      insertRepeatBlock: (attrs: { key: string }) =>
        ({ commands }: { commands: { insertContent: (value: unknown) => boolean } }) =>
          commands.insertContent({
            type: this.name,
            attrs,
            // One empty paragraph, so the block is something an author can put
            // a cursor in the moment it is inserted. An empty container is a
            // block nobody can type into and that looks like nothing happened.
            content: [{ type: 'paragraph' }],
          }),
    } as Partial<RawCommands>
  },
})

/**
 * A block of narrative the OPERATOR may amend while filling the document in.
 *
 * Everything else on a document form is fixed by the template and the values
 * are what vary. This is the exception, and it exists because a real agreement
 * has terms: the template proposes them, the parties negotiate them, and what
 * gets signed is what they settled on — not what was authored months earlier by
 * somebody who was not in the room.
 *
 * It is a CONTAINER, not an atom, for the same reason `RepeatBlock` is: what an
 * author puts inside it is ordinary document content, so a clause list, a table
 * of fees and a caution all lay out and paginate through the code everything
 * else uses. And like every node here it carries only a KEY — whether it is
 * editable at run time, who may amend it and whether an amendment needs a
 * reason are schema questions, and putting them on the node would let somebody
 * editing body text change a control.
 *
 * The edited content is stored per DOCUMENT, never back into the template: the
 * template is what the next document starts from, and one negotiation must not
 * rewrite the terms everybody else is offered.
 */
export const NarrativeBlock = Node.create({
  name: 'narrativeBlock',
  group: 'block',
  content: 'block+',
  defining: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      key: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-key') || '',
        renderHTML: (attributes) => ({ 'data-key': String(attributes.key || '') }),
      },
      /** Shown to the operator above the box, e.g. "Terms and conditions". */
      label: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-label') || '',
        renderHTML: (attributes) => (attributes.label
          ? { 'data-label': String(attributes.label) } : {}),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'div[data-narrative-block]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, {
      'data-narrative-block': 'true', class: 'twa-doc-narrative',
    }), 0]
  },

  addCommands() {
    return {
      insertNarrativeBlock: (attrs: { key: string; label?: string }) =>
        ({ commands }: { commands: { insertContent: (value: unknown) => boolean } }) =>
          commands.insertContent({
            type: this.name,
            attrs,
            content: [{ type: 'paragraph' }],
          }),
    } as Partial<RawCommands>
  },
})

/**
 * The step a heading or list item OPENS.
 *
 * Added to the nodes that already carry the document's numbering rather than
 * introducing a step node of its own — see the note at the top of this block.
 * `null` on every existing document, so nothing round-trips differently.
 */
export const StepMark = Extension.create({
  name: 'stepMark',

  addGlobalAttributes() {
    return [{
      types: ['heading', 'listItem', 'paragraph'],
      attributes: {
        stepKey: {
          default: null,
          parseHTML: (element: HTMLElement) => element.getAttribute('data-step') || null,
          renderHTML: (attributes: Record<string, unknown>) => (attributes.stepKey
            ? { 'data-step': String(attributes.stepKey) } : {}),
        },
      },
    }]
  },
})

/**
 * A SECTION of a composed document — the piece that is filled in and signed
 * off on its own.
 *
 * A batch manufacturing record is not one form: it is a work-order header, a
 * dispensing sheet, and then whichever process sections the product's route
 * calls for, each completed on a different day by a different person and each
 * locked once its own signatures are in. What makes it one document is that
 * the pieces are assembled in a fixed order, numbered, and signed as a whole
 * at the end.
 *
 * **The node is a PLACEHOLDER while authoring and a CONTAINER at run time.**
 * A master template places `sectionBlock` with only a `key`; the engine
 * replaces each one with the section instances that key produced, filling in
 * `instanceKey`, `sectionNo`, `state` and `versionNo` and putting the
 * section's own body inside. So an author lays out "the process sections go
 * here" once and gets four of them on a four-step route.
 *
 * It is ANCHOR-OPAQUE, like `repeatBlock` and `narrativeBlock`, and for a
 * third reason: one authored block emits a different NUMBER of blocks per
 * document, so a body offset computed through it would be wrong for every
 * document with a different route.
 *
 * Everything that governs a section — which template, which revision, whether
 * it may still be written to — lives in the engine's own tables. The node
 * carries only what has to be DRAWN.
 */
export const SectionBlock = Node.create({
  name: 'sectionBlock',
  group: 'block',
  content: 'block*',
  defining: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      /** The DECLARATION on the master — what an author placed. */
      key: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-key') || '',
        renderHTML: (attributes) => ({ 'data-key': String(attributes.key || '') }),
      },
      /** The section INSTANCE, and the namespace its fields live in. Empty
       *  while authoring: the engine stamps it when it assembles. */
      instanceKey: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-instance') || '',
        renderHTML: (attributes) => (attributes.instanceKey
          ? { 'data-instance': String(attributes.instanceKey) } : {}),
      },
      sectionNo: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-section-no') || '',
        renderHTML: (attributes) => (attributes.sectionNo
          ? { 'data-section-no': String(attributes.sectionNo) } : {}),
      },
      title: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-title') || '',
        renderHTML: (attributes) => (attributes.title
          ? { 'data-title': String(attributes.title) } : {}),
      },
      /** `open` | `signed` | `superseded`. Drawn, never trusted: the engine
       *  refuses the write, and this only decides how the box looks. */
      state: {
        default: 'open',
        parseHTML: (element) => element.getAttribute('data-state') || 'open',
        renderHTML: (attributes) => ({
          'data-state': String(attributes.state || 'open'),
        }),
      },
      versionNo: {
        default: 1,
        parseHTML: (element) => Number(element.getAttribute('data-version') || 1),
        renderHTML: (attributes) => ({
          'data-version': String(attributes.versionNo || 1),
        }),
      },
      supersededReason: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-superseded') || '',
        renderHTML: (attributes) => (attributes.supersededReason
          ? { 'data-superseded': String(attributes.supersededReason) } : {}),
      },
      /** Sections of a controlled document start on their own page by
       *  default, as they do on paper. A short sub-section may turn it off. */
      pageBreak: {
        default: true,
        parseHTML: (element) => element.getAttribute('data-page-break') !== 'false',
        renderHTML: (attributes) => (attributes.pageBreak === false
          ? { 'data-page-break': 'false' } : {}),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'div[data-section-block]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, {
      'data-section-block': 'true', class: 'twa-doc-section',
    }), 0]
  },

  addCommands() {
    return {
      insertSectionBlock: (attrs: { key: string }) =>
        ({ commands }: { commands: { insertContent: (value: unknown) => boolean } }) =>
          commands.insertContent({
            type: this.name,
            attrs,
            content: [{ type: 'paragraph' }],
          }),
    } as Partial<RawCommands>
  },
})
